/**
 * The loading screen, which lives in index.html and not in React.
 *
 * WHY IT IS STATIC HTML. Building a fab blocks the main thread: `new
 * FabScene()` runs inside FabCanvas's first render and the shader prewarm
 * runs in its layout effect, and nothing paints between the module script
 * starting and the first frame - several seconds on the integrated GPU this
 * project promises to run on. React cannot show a loading state it has not
 * had a turn to render, so the state is in the document before any script
 * runs, and all this file does is switch it off - and back on again for the
 * moment between choosing the other fab and the reload taking the page away.
 *
 * WHY THE BAR IS INDETERMINATE. It cannot report progress, because the work
 * it covers is one synchronous stretch with no yields in it; a percentage
 * would be a guess dressed as a measurement. What it can do is keep MOVING
 * while the thread is blocked, which a `transform` keyframe does because
 * Chrome runs it on the compositor. That is the whole design: honest about
 * not knowing how far along it is, and never frozen.
 */

import { tOr } from '../../i18n/i18n'
import { planIdOf, type HallLayout } from '../../fab/hall'
import { getPlan } from '../../fab/floorPlan'

const FADE_MS = 320

let hideTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Put the screen back up, for the reload into the other fab. The name is the
 * same string the menu shows for that fab, so the two cannot drift.
 */
export function showLoading(fab: HallLayout): void {
  const el = document.getElementById('loading')
  if (!el) return
  const text = document.getElementById('loading-text')
  if (text) {
    // A fab somebody built is named by its author, and that is the name to
    // put up: they have just pressed a button with it on.
    const planId = planIdOf(fab)
    const name = planId
      ? getPlan(planId)?.name ?? tOr('author.NewFloor', 'New floor')
      : fab === 'ballroom'
        ? tOr('menu.HallBallroom', 'Fab B')
        : tOr('menu.HallBayChase', 'Fab A')
    text.textContent = `${tOr('menu.Loading', 'Loading')} ${name}…`
  }
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = null
  el.classList.remove('done')
  el.hidden = false
}

/**
 * Put the screen up and run the blocking work only once the screen has been
 * PAINTED. The switch is one synchronous stretch, and a call that shows the
 * screen and rebuilds the hall in the same task never paints the screen -
 * the student sees the menu freeze and the other fab appear. Two animation
 * frames: the first runs before that frame's paint, the second after it, so
 * by the second the bar is on screen and its compositor animation is
 * running. A timer covers a hidden tab, where frames never come.
 */
export function showLoadingThen(fab: HallLayout, work: () => void): void {
  showLoading(fab)
  let done = false
  const go = (): void => {
    if (done) return
    done = true
    work()
  }
  requestAnimationFrame(() => requestAnimationFrame(go))
  setTimeout(go, 400)
}

/** The fab is drawn. Fade the screen and take it out of the way. */
export function hideLoading(): void {
  const el = document.getElementById('loading')
  if (!el || el.hidden) return
  el.classList.add('done')
  hideTimer = setTimeout(() => {
    el.hidden = true
    hideTimer = null
  }, FADE_MS)
}
